import { useMemo, useState } from 'react';
import { BoardPick, BoardSearch, useBoard } from '@/features/project-board/BoardProvider';
import type { DrawerPayload } from '@/features/project-board/components/DetailDrawer';
import { DrawerPayloads } from '@/features/project-board/drawer-payloads';
import { EpicMembership } from '@/features/project-board/identity';
import { QaCalendar, type QaCalendarSegment } from '@/features/project-board/qa-calendar';
import { QaScheduleLabel } from '@/features/project-board/qa-schedule';
import { OwnerRoster } from '@/features/project-board/owners';
import type { Epic, QaTicket, QaTicketStatus } from '@/features/project-board/types';
import { ViewFrame } from '@/features/project-board/views/ViewFrame';
import { ViewModeToggle, type ViewModeOption } from '@/shared/ui/ViewModeToggle';

type QaRow = QaTicket & { source: string; epic: Epic; documentPath: string };
type QaMode = 'stage' | 'table' | 'calendar';
const EDGE: Record<QaTicketStatus, string> = { 'No Run': 'border-l-progress', Pass: 'border-l-done', Fail: 'border-l-amber-500', Block: 'border-l-slate-700', 'N/A': 'border-l-violet-500', Skip: 'border-l-slate-400' };
const MODES: ReadonlyArray<ViewModeOption<QaMode>> = [
  { key: 'stage', label: '상태', glyph: '▤' },
  { key: 'table', label: '테이블', glyph: '☰' },
  { key: 'calendar', label: '달력', glyph: '▦' },
];

/**
 * 에픽 색 — 클래스 이름을 조립하지 않고 표에서 고른다(Tailwind가 조립된 이름을 못 찾는다).
 * 검증 기간은 칠한 막대, 배포 목표일은 같은 색의 테두리 칩이다.
 */
const TONES: ReadonlyArray<{ review: string; release: string }> = [
  { review: 'bg-sky-100 text-sky-900', release: 'border-sky-500 text-sky-800' },
  { review: 'bg-amber-100 text-amber-900', release: 'border-amber-500 text-amber-800' },
  { review: 'bg-emerald-100 text-emerald-900', release: 'border-emerald-500 text-emerald-800' },
  { review: 'bg-violet-100 text-violet-900', release: 'border-violet-500 text-violet-800' },
  { review: 'bg-rose-100 text-rose-900', release: 'border-rose-500 text-rose-800' },
  { review: 'bg-teal-100 text-teal-900', release: 'border-teal-500 text-teal-800' },
];

const WEEKDAYS = ['월', '화', '수', '목', '금', '토', '일'];
/**
 * 레인 순서 — 아직 안 본 것(`No Run`) 다음이 **손이 가야 하는 것**(`Fail`·`Block`)이고 `Pass`는 그 뒤다.
 * `No Run`을 맨 앞에 두고, 나머지는 색인의 결과 합산 우선순위(`QaTicketAssembler.statusOf`)와 같은
 * 순서다 — 한 항목에 여러 결과가 섞였을 때 티켓이 앉는 레인과 그 레인이 놓인 자리가 같은 기준으로 읽힌다.
 */
const QA_STATUS_ORDER: readonly QaTicketStatus[] = ['No Run', 'Fail', 'Block', 'Pass', 'N/A', 'Skip'];

/** 결과 HTML의 TC-ID. 이 필드가 생기기 전에 색인된 행엔 없다. */
const tcIdsOf = (ticket: QaTicket): string[] => ticket.details.tcIds ?? [];

function statusColumns(tickets: readonly QaTicket[]): QaTicketStatus[] {
  const extra = tickets
    .map((ticket) => ticket.status)
    .filter((status): status is QaTicketStatus => !QA_STATUS_ORDER.includes(status));
  return [...QA_STATUS_ORDER, ...Array.from(new Set(extra))];
}

export function QaView(): React.JSX.Element {
  const { index, filters } = useBoard();
  const [mode, setMode] = useState<QaMode>('stage');
  const rows = useMemo(() => index?.qaTickets.flatMap((ticket) => {
    const epic = index.epics.find((candidate) => candidate.source === ticket.source && candidate.slug === ticket.epicSlug);
    return epic ? [{ ...ticket, epic }] : [];
  }) ?? [], [index]);

  // QA 티켓엔 담당자 칸이 없어 에픽 화면과 같은 기준(그 에픽의 사람인가)으로 거른다.
  const filtered = useMemo(() => rows.filter((row) =>
    BoardPick.allows(filters.epicSlugs, row.epic.slug) &&
    BoardPick.allowsAny(filters.owners, (owner) => OwnerRoster.isEpicPerson(row.epic, index?.tasks ?? [], owner)) &&
    BoardSearch.matches(`${row.id} ${row.title} ${row.epic.title} ${row.epic.slug}`, filters.query),
  ), [filters.epicSlugs, filters.owners, filters.query, index, rows]);

  // 달력은 티켓이 아니라 에픽 단위다 — 일정은 역기획서(에픽)에 적히고, QA가 티켓을 만들기 전에도 잡힌다.
  const scheduledEpics = useMemo(() => (index?.epics ?? []).filter((epic) =>
    epic.qaSchedule !== null &&
    BoardPick.allows(filters.epicSlugs, epic.slug) &&
    BoardPick.allowsAny(filters.owners, (owner) => OwnerRoster.isEpicPerson(epic, index?.tasks ?? [], owner)) &&
    BoardSearch.matches(`${epic.title} ${epic.slug}`, filters.query),
  ), [filters.epicSlugs, filters.owners, filters.query, index]);

  const calendar = mode === 'calendar';
  return (
    <ViewFrame
      scope="qa"
      countLabel={calendar ? `QA 일정 ${scheduledEpics.length}건` : `${filtered.length}개 QA`}
      isEmpty={calendar ? false : filtered.length === 0}
      emptyMessage="조건에 맞는 QA가 없다."
      toolbar={<ViewModeToggle options={MODES} value={mode} onChange={setMode} />}
      render={(open) =>
        calendar ? (
          <CalendarBoard
            epics={scheduledEpics}
            open={(epic) => open(DrawerPayloads.epic(epic, EpicMembership.tasksOf(index?.tasks ?? [], epic)))}
          />
        ) : mode === 'stage' ? (
          <StageBoard rows={filtered} open={open} />
        ) : (
          <Table rows={filtered} open={open} />
        )
      }
    />
  );
}

function StageBoard({ rows, open }: { rows: QaRow[]; open: (payload: DrawerPayload) => void }): React.JSX.Element {
  const columns = statusColumns(rows);
  return <div className="grid grid-flow-col auto-cols-[minmax(280px,1fr)] items-start gap-3 overflow-x-auto pb-2">
    {columns.map((status) => <div key={status} className="min-w-[280px] rounded-lg border border-line-soft bg-[#f0f3f6]">
      <div className="flex items-center gap-1.5 border-b border-line-soft px-3 py-2.5 text-xs font-semibold text-muted">{status}<span className="ml-auto rounded-full bg-surface px-1.5 text-[11px]">{rows.filter((row) => row.status === status).length}</span></div>
      <div className="flex max-h-[calc(100vh-230px)] flex-col gap-2 overflow-y-auto p-2.5">{rows.filter((row) => row.status === status).map((row) => <Card key={`${row.source}:${row.epic.slug}:${row.id}`} row={row} open={open} />)}</div>
    </div>)}
  </div>;
}

function Card({ row, open }: { row: QaRow; open: (payload: DrawerPayload) => void }): React.JSX.Element {
  return <article onClick={() => open(DrawerPayloads.qa(row, row.epic, row.documentPath))} className={`cursor-pointer rounded-md border border-line border-l-[3px] bg-surface p-2.5 hover:border-accent ${EDGE[row.status]}`}>
    <div className="line-clamp-3 text-[13px] leading-snug">{row.title}</div>
    <div className="mt-1 text-[11px] text-muted">{row.epic.title}</div>
    {QaScheduleLabel.brief(row.epic.qaSchedule) && <div className="mt-1 text-[11px] text-muted">{QaScheduleLabel.brief(row.epic.qaSchedule)}</div>}
    {tcIdsOf(row).length > 0 && <div className="mt-1 font-mono text-[10px] text-accent">{tcIdsOf(row).join(' · ')}</div>}
    <footer className="mt-2 flex items-center gap-1.5"><span className="font-mono text-[10px] text-faint">{row.id}</span><span className="rounded bg-line-soft px-1 py-0.5 text-[10px] text-muted">{row.kind} · {row.priority}</span></footer>
  </article>;
}

function Table({ rows, open }: { rows: QaRow[]; open: (payload: DrawerPayload) => void }): React.JSX.Element {
  return <div className="rounded-lg border border-line bg-surface"><table className="w-full table-fixed border-collapse"><thead><tr className="bg-[#f0f3f6] text-left text-xs font-semibold text-muted"><th className="w-[110px] px-2.5 py-2.5">QA ID</th><th className="w-[130px] px-2.5 py-2.5">TC-ID</th><th className="px-2.5 py-2.5">QA</th><th className="w-[250px] px-2.5 py-2.5">에픽</th><th className="w-[170px] px-2.5 py-2.5">QA 일정</th><th className="w-[100px] px-2.5 py-2.5">상태</th></tr></thead><tbody>{rows.map((row) => <tr key={`${row.source}:${row.epic.slug}:${row.id}`} onClick={() => open(DrawerPayloads.qa(row, row.epic, row.documentPath))} className="cursor-pointer border-b border-line-soft hover:bg-canvas [&>td]:px-2.5 [&>td]:py-2.5"><td className="font-mono text-[10px] text-muted">{row.id}</td><td className="font-mono text-[10px] text-accent">{tcIdsOf(row).join(', ')}</td><td className="text-[13px] font-semibold">{row.title}</td><td><div className="font-semibold">{row.epic.title}</div><div className="font-mono text-[10px] text-faint">{row.epic.slug}</div></td><td className="text-[12px] text-muted">{QaScheduleLabel.brief(row.epic.qaSchedule) ?? '—'}</td><td className="text-[12px]">{row.status}</td></tr>)}</tbody></table></div>;
}

/**
 * 월 달력 — 에픽의 검증 기간을 막대로, 배포 목표일을 칩으로 놓는다. 막대를 누르면 에픽 상세가 열리고,
 * 거기서 역기획서를 열어 일정을 고친다.
 *
 * 일정이 없는 달도 비우지 않고 달력을 그린다 — 빈 달이 곧 "이 달에 잡힌 QA가 없다"는 정보다.
 */
function CalendarBoard({ epics, open }: { epics: Epic[]; open: (epic: Epic) => void }): React.JSX.Element {
  const today = QaCalendar.today();
  const [month, setMonth] = useState(() => QaCalendar.monthOf(today));
  const events = useMemo(() => QaCalendar.eventsOf(epics), [epics]);
  const weeks = useMemo(() => QaCalendar.weeksOf(month), [month]);
  const navButton = 'rounded-md border border-line bg-surface px-2 py-1 text-[12px] hover:bg-line-soft';

  return (
    <div className="rounded-lg border border-line bg-surface">
      <div className="flex items-center gap-2 border-b border-line px-3 py-2">
        <button type="button" onClick={() => setMonth((current) => QaCalendar.shiftMonth(current, -1))} className={navButton}>‹</button>
        <div className="min-w-[88px] text-center text-[13px] font-semibold">{month.replace('-', '년 ')}월</div>
        <button type="button" onClick={() => setMonth((current) => QaCalendar.shiftMonth(current, 1))} className={navButton}>›</button>
        <button type="button" onClick={() => setMonth(QaCalendar.monthOf(today))} className={navButton}>오늘</button>
        <span className="ml-auto text-[11px] text-muted">막대 = 검증 기간 · 테두리 칩 = 배포 목표일</span>
      </div>
      <div className="grid grid-cols-7 border-b border-line-soft text-center text-[11px] font-semibold text-muted">
        {WEEKDAYS.map((label, index) => (
          <div key={label} className={`py-1.5 ${index >= 5 ? 'bg-canvas' : ''}`}>{label}</div>
        ))}
      </div>
      {weeks.map((week) => (
        <CalendarWeek key={week[0]} week={week} month={month} today={today} segments={QaCalendar.segmentsOf(week, events)} open={open} />
      ))}
    </div>
  );
}

function CalendarWeek({
  week,
  month,
  today,
  segments,
  open,
}: {
  week: string[];
  month: string;
  today: string;
  segments: QaCalendarSegment[];
  open: (epic: Epic) => void;
}): React.JSX.Element {
  const lanes = segments.reduce((max, segment) => Math.max(max, segment.lane + 1), 0);
  return (
    <div className="relative grid min-h-[96px] grid-cols-7 border-b border-line-soft last:border-b-0" style={{ gridTemplateRows: `24px ${'22px '.repeat(lanes)}1fr` }}>
      {/* 날짜 칸 배경 — 일정 막대는 그 위 같은 격자에 겹쳐 놓인다. */}
      {week.map((date, index) => (
        <div
          key={date}
          className={`border-r border-line-soft last:border-r-0 ${index >= 5 ? 'bg-canvas' : ''}`}
          style={{ gridColumn: index + 1, gridRow: `1 / span ${lanes + 2}` }}
        >
          <div className={`px-1.5 pt-1 text-right text-[11px] ${
            date === today ? 'font-bold text-accent' : QaCalendar.monthOf(date) === month ? 'text-ink' : 'text-faint'
          }`}>
            {Number(date.slice(8))}
          </div>
        </div>
      ))}
      {segments.map((segment) => {
        const tone = TONES[QaCalendar.toneOf(segment.event.epic, TONES.length)];
        const review = segment.event.kind === 'review';
        return (
          <button
            key={`${segment.event.kind}:${segment.event.epic.source}:${segment.event.epic.slug}`}
            type="button"
            onClick={() => open(segment.event.epic)}
            title={`${segment.event.epic.title} — ${review ? `검증 ${segment.event.start} ~ ${segment.event.end}` : `배포 목표 ${segment.event.start}`}`}
            className={`mx-0.5 my-px cursor-pointer truncate px-1.5 text-left text-[11px] leading-5 hover:brightness-95 ${
              review ? tone.review : `border bg-surface ${tone.release}`
            } ${segment.continuesBefore ? 'rounded-l-none' : 'rounded-l'} ${segment.continuesAfter ? 'rounded-r-none' : 'rounded-r'}`}
            style={{ gridColumn: `${segment.column + 1} / span ${segment.span}`, gridRow: segment.lane + 2 }}
          >
            {review ? 'QA: ' : '배포: '}
            {segment.event.epic.title}
          </button>
        );
      })}
    </div>
  );
}
